import { useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { FilterConfig } from "@/components/ChipFilterGroup/ChipFilterGroup";
import { BaseEntity } from "@/components/EntityPickerModal/EntityPickerModal";
import { fetchCityFilterOptions } from "@/modules/stochastic/features/DashboardPage/api/fetchCityFilterOptions/fetchCityFilterOptionsApi";
import { CityFilterOption } from "@/modules/stochastic/features/DashboardPage/api/fetchCityFilterOptions/types";
import { fetchTradesFilterOptions } from "@/modules/stochastic/features/DashboardPage/api/fetchTradeFilterOptions/fetchTradeFilterOptionsApi";
import { TradeFilterOption } from "@/modules/stochastic/features/DashboardPage/api/fetchTradeFilterOptions/types";
import { fetchYearFilterOptions } from "@/modules/stochastic/features/DashboardPage/api/fetchYearFilterOptions/fetchYearFilterOptionsApi";
import { YearFilterOption } from "@/modules/stochastic/features/DashboardPage/api/fetchYearFilterOptions/types";
import {
  DashboardFiltersFormData,
  MEMBER_TYPE_OPTIONS,
  DEFAULT_MEMBER_TYPE,
} from "@/modules/stochastic/features/DashboardPage/hooks/dashboardFiltersFormSchema";

export const useStochasticDashboardFilters = (
  chartFiltersForm: UseFormReturn<DashboardFiltersFormData>,
) => {
  const { watch, setValue } = chartFiltersForm;

  const filterConfigs: FilterConfig<BaseEntity>[] = useMemo(
    () => [
      {
        key: "cities",
        label: "Cities",
        type: "multi" as const,
        color: "red" as const,
        entityNameSingular: "City",
        entityNamePlural: "Cities",
        fetchEntities: async ({
          searchTerm,
          page,
          pageSize,
        }: {
          searchTerm: string;
          page: number;
          pageSize: number;
        }) => {
          const response = await fetchCityFilterOptions({
            searchTerm,
            page,
            pageSize,
            sortBy: "city",
            sortOrder: "ASC",
          });
          const { data } = response;
          const totalCount = response.meta?.totalCount ?? data.length;

          return {
            data: data.map((cfo: CityFilterOption) => ({
              id: String(cfo.id),
              name: cfo.name,
            })),
            totalCount,
          };
        },
      },
      {
        key: "trades",
        label: "Trades",
        type: "multi" as const,
        color: "blue" as const,
        entityNameSingular: "Trade",
        entityNamePlural: "Trades",
        fetchEntities: async ({
          searchTerm,
          page,
          pageSize,
        }: {
          searchTerm: string;
          page: number;
          pageSize: number;
        }) => {
          const response = await fetchTradesFilterOptions({
            searchTerm,
            page,
            pageSize,
            sortBy: "name",
            sortOrder: "ASC",
          });
          const { data } = response;
          const totalCount = response.meta?.totalCount ?? data.length;

          return {
            data: data.map((tfo: TradeFilterOption) => ({
              id: String(tfo.id),
              name: tfo.name,
            })),
            totalCount,
          };
        },
      },
      {
        key: "years",
        label: "Years",
        type: "multi" as const,
        color: "green" as const,
        entityNameSingular: "Year",
        entityNamePlural: "Years",
        fetchEntities: async ({
          searchTerm,
          page,
          pageSize,
        }: {
          searchTerm: string;
          page: number;
          pageSize: number;
        }) => {
          const response = await fetchYearFilterOptions({
            searchTerm,
            page,
            pageSize,
            sortBy: "year",
            sortOrder: "DESC",
          });
          const { data } = response;
          const totalCount = response.meta?.totalCount ?? data.length;

          return {
            data: data.map((yfo: YearFilterOption) => ({
              id: String(yfo.id),
              name: yfo.name,
            })),
            totalCount,
          };
        },
      },
      {
        key: "memberType",
        label: "Customer Type",
        type: "single" as const,
        color: "purple" as const,
        entityNameSingular: "Customer Type",
        entityNamePlural: "Customer Types",
        defaultValue: DEFAULT_MEMBER_TYPE,
        fetchEntities: async () => ({
          data: MEMBER_TYPE_OPTIONS,
          totalCount: MEMBER_TYPE_OPTIONS.length,
        }),
      },
    ],
    [],
  );

  const filterValues = watch();

  const handleFilterChange = useCallback(
    (key: string, value: BaseEntity[] | BaseEntity | null) => {
      setValue(key as keyof DashboardFiltersFormData, value as never);
    },
    [setValue],
  );

  const handleFilterClearAll = useCallback(() => {
    setValue("cities", []);
    setValue("trades", []);
    setValue("years", []);
    setValue("memberType", DEFAULT_MEMBER_TYPE);
  }, [setValue]);

  return {
    filterConfigs,
    filterValues,
    handleFilterChange,
    handleFilterClearAll,
  };
};
